// Spending rope that has been launched but not wound yet.
//
// ── what this is ──────────────────────────────────────────────────────────
//
// The wizard's twin of a stock reel, one step earlier in time. A phantom
// execution is a claim on metres that EXIST — born COMPLETED, because whoever
// wound that reel measured them (`feedback_phantom_reel_claims`). This is a
// claim on metres that WILL exist: the rope is already launched and already in
// the DTB1250's queue, so the cable does not schedule anything, it just becomes
// a customer for part of that drum's output.
//
//   *"Cand folosesc available reels from stock … si aleg sa folosesc produsele
//   care sunt ACTIVE in PRODUCTIE, pending sau deja in stock, nu mai trebuie sa
//   creeze nimic nicaieri, trebuie doar sa le foloseasca pe alea."*
//   *"pentru ca produsele COPPER ROPE FLEXIBLE erau deja lansate."* (2026-11-05)
//
// So the execution carries NO MACHINE. `buildQueueEntriesForJobs` skips any task
// without one (`if (!task.machineId) continue`), which means nothing reaches a
// machine queue by construction rather than by a special case. The donor's plan,
// lot, task and queue entry are all untouched; the server writes one
// `reservedBy` row on its token and that is the whole footprint.
// `redis_dev/helpers/ropeClaims.js` is the other half.
//
// ── what it replaced, and why ─────────────────────────────────────────────
//
// It was built as a SHARE first (`exec.mergeOnto`, `drumShares.js`) because that
// mechanism existed. A share is several products' output on ONE bobbin: the
// drum's plan becomes the sum of its shares, so the donor's plan and lot are
// re-typed and every share needs a queue entry for the worker's rollover. Right
// for the case it was built for — the operator winds 20 000 m instead of 15 000,
// so the next lot goes on the same bobbin — and wrong for rope already launched.
//
// The 2026-11-04 launch showed how wrong: dividing two 30 000 m drums re-planned
// both COPPER_ROPE jobs to a lot of ZERO, and the shares arrived in the DTB1250
// queue as two separate MYYM runs — new work, on metres already being wound.
// *"A BAGAT SI MYYM 5x6, MYYM 3x6 ca produse separate, nu in merge."*
//
// A claim cannot do that: there is no division to get wrong and nothing to
// enqueue.
//
// Pure, and mirrored into tests/.generated by tests/tools/mirror.mjs.

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Metres claimed off each drum by this selection, summed per drum. */
export function takenPerDrum(selections = []) {
  const out = new Map();
  for (const s of selections) {
    if (!s?.tokenId) continue;
    out.set(String(s.tokenId), (out.get(String(s.tokenId)) || 0) + num(s.useLength));
  }
  return out;
}

/**
 * The pre-stranding executions a confirmed selection becomes.
 *
 * @param selections `[{ tokenId, jobId, taskId, useLength, … }]` as
 *                   `/phantoms/incoming-ropes` reported the drums
 * @param existing   the phase's current executions, for id stability
 * @param newId      () => id, injectable so a test is not timing-dependent
 */
export function buildRopeClaimExecs(selections = [], { existing = [], newId } = {}) {
  const mint = typeof newId === 'function' ? newId : () => Date.now() + Math.random();

  return selections.map(sel => {
    // One drum is one execution, so the drum is the stable thing to key on. The
    // next phase references these ids in `fromUpstreams`, so a regenerated id
    // orphans every downstream allocation — the bug the stock reels had in
    // October, where `Used` on the incoming cards dropped back to 0 after a
    // second confirm.
    const prev = (existing || []).find(e => e?.ropeClaim?.tokenId === sel.tokenId);

    return {
      id: prev?.id ?? mint(),

      // NO MACHINE. This is the single most important field on the row: the
      // rope is already launched and already queued, so this product schedules
      // nothing. A machine here would put a second run on the DTB1250 for
      // metres that are already being wound.
      machineId: '',

      plannedLength: num(sel.useLength),

      // The reel these metres will come off is the donor's, so the drum is not
      // this product's to restate — but it is worth carrying for the card, the
      // same way a phantom execution carries its reel's spec.
      drum_material: sel.drum_material || '',
      drum_diameter: sel.drum_diameter || '',

      // The POINTER, exactly as a phantom carries `phantomId`.
      isRopeClaim: true,
      ropeClaim: {
        tokenId: sel.tokenId,
        jobId: sel.jobId,
        taskId: sel.taskId
      },

      // ── WHAT THE DRUM IS, kept ON the execution ──────────────────────────
      //
      // So the locked card can say where the metres come from without
      // re-reading the factory. Display only: the launch re-reads every figure
      // off the donor's token before it writes anything, because a wizard left
      // open is a stale copy of the floor.
      claimStatus: sel.status || null,
      claimLabel: sel.productLabel || null,
      claimJobCode: sel.jobCode || null,
      claimDrumPlanned: num(sel.plannedLength),
      claimDrumProduced: num(sel.producedLength),
      claimMachineId: sel.machineId || null,
      claimSection: sel.section ?? null,
      claimWireType: sel.wireType || null,

      // WHICH rope this drum feeds. A mixed pre-stranding draws its cards with
      // `e.ropeIndex === idx`, and `undefined === 0` is false — without this
      // the execution exists, moves the phase bar and appears under no rope.
      ...(sel.ropeIndex != null ? { ropeIndex: sel.ropeIndex } : {})
    };
  });
}

/** Is this execution a claim on a drum another job is winding? */
export function isClaimExec(exec) {
  return !!exec?.isRopeClaim && !!exec?.ropeClaim?.tokenId;
}

/**
 * Is this execution's source already decided — a reel off the shelf, or a claim
 * on a drum being wound?
 *
 * ONE predicate, because the wizard draws a locked card for it in five places.
 * `PhantomExecutionCard` carries the scar of the last time this was spread out:
 * a fourth site knew nothing about phantoms and drew an ordinary card with an
 * empty machine and an empty length. A claim arrived the same way on 2026-11-04
 * — *"imi pune card clasic de executie. Trebuie tot card de phantom!"* — so the
 * predicate lives here and a test counts the sites.
 *
 * `mergeOnto` on its own is deliberately NOT included: a division typed in
 * `ShareOntoDrumPicker` belongs to an ordinary execution that keeps its machine
 * and its length, and locking it would take both fields away.
 */
export function isLockedSourceExec(exec) {
  return (!!exec?.isPhantomSource && !!exec?.phantomId) || isClaimExec(exec);
}

/**
 * The phase's executions after a confirm: every claim replaced, the rest kept.
 *
 * Same shape as the stock confirm, which replaces every phantom execution —
 * anything the planner typed by hand survives either way.
 */
export function replaceClaimExecs(existing = [], claimExecs = []) {
  const kept = (existing || []).filter(e => !isClaimExec(e));
  return [...kept, ...claimExecs];
}

/** What this phase has already claimed, per drum — to re-seed the picker. */
export function ownClaimMap(executions = []) {
  const out = {};
  for (const e of executions || []) {
    if (!isClaimExec(e)) continue;
    out[e.ropeClaim.tokenId] = (out[e.ropeClaim.tokenId] || 0) + num(e.plannedLength);
  }
  return out;
}

/** Every rope claim in the launch, in both execution shapes. */
export function collectClaimExecs(standardData = [], { productId = null, phaseName = null } = {}) {
  const out = [];
  for (const prod of standardData || []) {
    for (const ph of prod?.phases || []) {
      const own =
        productId != null &&
        String(prod?._id) === String(productId) &&
        String(ph?.name) === String(phaseName);

      const execs = [
        ...(ph?.execution || []),
        // A conductor-isolation phase keeps its executions per conductor; a
        // walk that only read `phase.execution` would count a claim there as
        // nobody's.
        ...(ph?.conductors || []).flatMap(c => c?.execution || [])
      ];

      for (const exec of execs) {
        if (!isClaimExec(exec)) continue;
        out.push({ prod, phaseName: ph?.name, exec, own });
      }
    }
  }
  return out;
}

/**
 * Who is on each drum being wound, across the WHOLE launch.
 *
 * Split the same way the stock reels are, and for the same reason: one drum can
 * be offered to several products in one submit, and the planner could not tell
 * an untouched one from one already spent. `otherAllocMap` is what OTHERS have
 * claimed — metres this phase may not have. `ownAllocMap` is this phase's own
 * claim, which it keeps, because a confirm REPLACES every claim on the phase.
 *
 * Reported 2026-11-04, after the MYYM 5x6 claimed 17 500 m of the turning drum:
 * *"cand merg sa aloc si pentru 3x6 … ma lasat sa iau tot 30000 desi eu am luat
 * deja 17500 din el."* The server refuses an over-claim at launch
 * (`planRopeClaims`); this is what stops the planner reaching that point.
 *
 * @param labelOf (product) => string — the canonical product label; one built
 *                by hand loses the earth of a `3x35+16`
 */
export function claimsAcrossLaunch(
  standardData = [],
  { productId = null, phaseName = null, labelOf = p => p?.name || '' } = {}
) {
  const otherAllocMap = {};
  const ownAllocMap = {};
  const takenBy = {};

  for (const { prod, phaseName: phName, exec, own } of collectClaimExecs(standardData, {
    productId,
    phaseName
  })) {
    const tid = String(exec.ropeClaim.tokenId);
    const metres = num(exec.plannedLength);
    const bucket = own ? ownAllocMap : otherAllocMap;
    bucket[tid] = (bucket[tid] || 0) + metres;
    (takenBy[tid] = takenBy[tid] || []).push({
      label: `${labelOf(prod)} · ${String(phName).replace(/_/g, ' ')}`,
      metres,
      own
    });
  }

  return { otherAllocMap, ownAllocMap, takenBy };
}

const ropeClaimSelection = {
  takenPerDrum,
  buildRopeClaimExecs,
  isClaimExec,
  isLockedSourceExec,
  replaceClaimExecs,
  ownClaimMap,
  collectClaimExecs,
  claimsAcrossLaunch
};
export default ropeClaimSelection;
